// @sonar-ignore
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  ReactNode, useContext, useMemo, lazy,
} from 'react';
import { useSelector } from 'react-redux';

import { useOrientationChange } from '@ezugi/hooks';
import { LazyResolver } from '@ezugi/ui/components/LazyResolver/LazyResolver';
import { DeviceInfo } from '@ezugi/utils';
import { isEvoWrapped } from '@ezugi/utils/src';

import ChipSelector from '../../components/ChipSelector';
import { canPlaceBetsSelector, isDisconnectedOrReconnectingSelector } from '../../store/selectors/round';
import { evoThemeClass } from '../../variants/components/evo/evo-theme.css';
import { BetToolbarContext } from './BetToolbarContext';
import { ButtonGroup, verticalButtonGroup } from './components/style';
import * as styles from './style.css.ts';

import type { ButtonProps } from './types';

isEvoWrapped() && assignInlineVars({
  [styles.mobileBettingToolbarWidthVar]: '100%',
});

const BetToolbarChipSelector = () => {
  const { vertical } = useContext(BetToolbarContext);
  const orientation = useOrientationChange();
  const isLandscape = orientation === 'landscape';
  const chipSelectorOrientation = vertical ?? (DeviceInfo.isHandheld && isLandscape);

  return (
    <ChipSelector
      className={clsx(styles.chipSelector, 'bet-toolbar__chip-selector')}
      vertical={chipSelectorOrientation}
    />
  );
};

const BetTools = ({ children, className }: {children: ReactNode, className?: string}) => {
  const { vertical } = useContext(BetToolbarContext);

  return (
    <div
      style={assignInlineVars({
        [styles.mobileBettingToolbarWidthVar]: isEvoWrapped() ? '100%' : 'auto',
      })}
      className={clsx(
        styles.betTools,
        {
          [styles.mobileBetTools]: DeviceInfo.isHandheld,
          [styles.verticalBetTools]: vertical,
        },
        className
      )}
    >
      {children}
    </div>
  );
};

export type BetToolbarProps = {
  children: ReactNode;
  vertical?: boolean;
  className?: string;
  externalShowHideBehaviour?: boolean;
};

const Left = ({ children }: {children: ReactNode}) => {
  const { vertical } = useContext(BetToolbarContext);
  const isHandheldVertical = DeviceInfo.isHandheld && vertical;

  return (
    <ButtonGroup
      className={clsx(
        { [verticalButtonGroup]: isHandheldVertical },
        'bet-toolbar__button-group--left',
      )}
      data-e2e="bet-toolbar__button-group--left"
    >
      {children}
    </ButtonGroup>
  );
};

const Right = ({ children }: {children: ReactNode}) => {
  const { vertical } = useContext(BetToolbarContext);
  const isHandheldVertical = DeviceInfo.isHandheld && vertical;

  return (
    <ButtonGroup
      data-e2e="bet-toolbar__button-group--right"
      className={clsx(
        { [verticalButtonGroup]: isHandheldVertical },
        'bet-toolbar__button-group--right'
      )}
    >
      {children}
    </ButtonGroup>
  );
};

export const BetToolbar = ({
  className,
  vertical = false,
  externalShowHideBehaviour = false,
  children,
  ...props
}: BetToolbarProps) => {
  const canPlaceBets = useSelector(canPlaceBetsSelector);
  const disconnected = useSelector(isDisconnectedOrReconnectingSelector);
  const contextValue = useMemo(() => ({ vertical }), [ vertical ]);

  if (disconnected || (!canPlaceBets && !externalShowHideBehaviour)) {
    return null;
  }

  return (
    <BetToolbarContext.Provider value={contextValue}>
      <div
        className={clsx(
          styles.container,
          'bet-toolbar',
          {
            [styles.mobile]: DeviceInfo.isHandheld,
            [styles.vertical]: vertical,
            [styles.hiddenClassName]: externalShowHideBehaviour,
            [evoThemeClass]: isEvoWrapped(),
          },
          className
        )}
        {...props}
        data-e2e="bet-toolbar"
        data-testid="bet-toolbar"
      >
        {children}
      </div>
    </BetToolbarContext.Provider>
  );
};

BetToolbar.Left = Left;
BetToolbar.Right = Right;

const EvoUndo = lazy(() => import('../../variants/components/evo/EvoBetToolbarButtons/UndoButton'));

const EzugiUndo = lazy(() => import('../../variants/components/ezugi/EzugiBetToolbarButtons/UndoButton'));

const EvoDouble = lazy(() => import('../../variants/components/evo/EvoBetToolbarButtons/DoubleButton'));

const EzugiDouble = lazy(() => import('../../variants/components/ezugi/EzugiBetToolbarButtons/DoubleButton'));

const EvoRebet = lazy(() => import('../../variants/components/evo/EvoBetToolbarButtons/RebetButton'));

const EzugiRebet = lazy(() => import('../../variants/components/ezugi/EzugiBetToolbarButtons/RebetButton'));

const EvoClear = lazy(() => import('../../variants/components/evo/EvoBetToolbarButtons/ClearButton'));

const EzugiClear = lazy(() => import('../../variants/components/ezugi/EzugiBetToolbarButtons/ClearButton'));

BetToolbar.Undo = (props: ButtonProps) => (
  <LazyResolver
    components={{
      evo: EvoUndo,
      ezugi: EzugiUndo,
    }}
    componentProps={props}
    variant={isEvoWrapped() ? 'evo' : 'ezugi'}
  />
);

BetToolbar.ChipSelector = BetToolbarChipSelector;
BetToolbar.Double = (props: ButtonProps) => (
  <LazyResolver
    components={{
      evo: EvoDouble,
      ezugi: EzugiDouble,
    }}
    componentProps={props}
    variant={isEvoWrapped() ? 'evo' : 'ezugi'}
  />
);

BetToolbar.Clear = (props: ButtonProps) => (
  <LazyResolver
    components={{
      evo: EvoClear,
      ezugi: EzugiClear,
    }}
    componentProps={props}
    variant={isEvoWrapped() ? 'evo' : 'ezugi'}
  />
);

BetToolbar.Rebet = (props: ButtonProps) => (
  <LazyResolver
    components={{
      evo: EvoRebet,
      ezugi: EzugiRebet,
    }}
    componentProps={props}
    variant={isEvoWrapped() ? 'evo' : 'ezugi'}
  />
);
BetToolbar.BetTools = BetTools;

const DoubleRebetButton = ({ className, hasLabel }: {className?: string, hasLabel?: boolean}) => (
  <>
    <BetToolbar.Double className={className} hasLabel={hasLabel} />
    <BetToolbar.Rebet className={className} hasLabel={hasLabel} />
  </>
);

BetToolbar.DoubleRebet = DoubleRebetButton;

type BetToolbarOverrides = {
  disableRebetOverride?: boolean;
  disableUndoOverride?: boolean;
  disableDoubleOverride?: boolean;
};

export type EzugiBetToolbarProps = Omit<BetToolbarProps, 'children'> & BetToolbarOverrides

export const EzugiBetToolbar = (
  { disableDoubleOverride,
    disableRebetOverride,
    disableUndoOverride,
    ...props }: EzugiBetToolbarProps
) => (
  <BetToolbar {...props}>
    <BetToolbar.BetTools>
      <BetToolbar.Left>
        <BetToolbar.Undo disabled={disableUndoOverride} />
      </BetToolbar.Left>
      <BetToolbar.ChipSelector />
      <BetToolbar.Right>
        <BetToolbar.Double disabled={disableDoubleOverride} />
        <BetToolbar.Rebet disabled={disableRebetOverride} />
      </BetToolbar.Right>
    </BetToolbar.BetTools>
  </BetToolbar>
);
